import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickOutside } from '../../lib/useClickOutside'

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  /** Selected option values, or `null` when every option is selected — the
   * prototype's convention for "no filter applied" (see mountMultiSelect in
   * usage-billing.html). An explicit `[]` means "nothing selected". */
  value: string[] | null
  onChange: (next: string[] | null) => void
  /** Hide the search box (`mountMultiSelect`'s `withSearch: false`). */
  withSearch?: boolean
  ariaLabel?: string
}

/** Matches the prototype's reusable `mountMultiSelect` filter dropdown:
 * a trigger showing "All" / "None" / "N selected", a popover with
 * a search box, Select all / Deselect all and a checkbox list. The search box
 * shows whatever the number of options, as the prototype's does (MS-996). Checking a box keeps the popover
 * open so several items can be picked in one go; only an outside click
 * closes it — same as the prototype and same as our topbar menus
 * (see useClickOutside), which is also how opening one of these while a
 * sibling dropdown is open closes the sibling first (mousedown fires
 * before the new trigger's click). */
export function MultiSelect({
  options,
  value,
  onChange,
  withSearch = true,
  ariaLabel,
}: Readonly<MultiSelectProps>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  useClickOutside(containerRef, () => setOpen(false), open)

  // If the option set itself changes (e.g. the tenant/user list refetches),
  // reset to "all selected" — mirrors the prototype's setOptions() behavior,
  // which always re-selects everything when a filter's option list changes.
  const optionKey = options.map((o) => o.value).join('\u0000')
  const prevOptionKey = useRef(optionKey)
  useEffect(() => {
    if (prevOptionKey.current !== optionKey) {
      prevOptionKey.current = optionKey
      onChange(null)
    }
  }, [optionKey, onChange])

  const selected = useMemo(
    () => (value === null ? new Set(options.map((o) => o.value)) : new Set(value)),
    [value, options]
  )
  const allSelected = value === null || selected.size === options.length
  let label: string
  if (allSelected) {
    label = t('common.all')
  } else if (selected.size === 0) {
    label = t('common.none')
  } else {
    label = t('common.nSelected', { count: selected.size })
  }

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  function commit(next: Set<string>) {
    onChange(next.size === options.length ? null : Array.from(next))
  }

  function toggle(v: string) {
    const next = new Set(selected)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    commit(next)
  }

  const showSearch = withSearch

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 text-[12.5px] px-2.5 py-1.5 border border-[var(--border)] rounded-md bg-[var(--bg-input)] text-[var(--text-primary)] ${open ? 'rounded-b-none border-[var(--accent)]' : ''}`}
      >
        <span className="truncate">{label}</span>
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 opacity-60"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        // The prototype's `.msel-pop`: exactly the trigger's width
        // (left/right 0), stuck to it with no gap, its top corners square
        // and its bottom ones rounded, so trigger and panel read as one
        // control. Inline shadow: Tailwind reads `shadow-[…]` with a
        // colour as a shadow colour.
        <div
          data-testid="filter-popover"
          className="absolute left-0 right-0 top-full -mt-px max-h-80 flex flex-col bg-[var(--bg-card)] border border-[var(--border)] rounded-b-[10px] z-50 p-2"
          style={{ boxShadow: '0 8px 28px rgba(0,0,0,.16)' }}
        >
          {showSearch && (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search')}
              className="text-[12px] px-2 py-1.5 mb-1.5 border border-[var(--border)] rounded-md bg-[var(--bg-input)] text-[var(--text-primary)]"
            />
          )}
          <div className="flex gap-3 mb-1.5 px-0.5 text-[11.5px] font-semibold">
            <button
              type="button"
              onClick={() => commit(new Set(options.map((o) => o.value)))}
              className="text-[var(--accent-text)] hover:underline"
            >
              {t('common.selectAll')}
            </button>
            <button type="button" onClick={() => commit(new Set())} className="text-[var(--accent-text)] hover:underline">
              {t('common.deselectAll')}
            </button>
          </div>
          <div className="overflow-y-auto flex flex-col">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-2 text-[12px] text-[var(--text-tertiary)]">{t('common.noMatches')}</div>
            ) : (
              filteredOptions.map((o) => (
                <label
                  key={o.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] text-[var(--text-primary)] cursor-pointer hover:bg-[var(--bg-hover)]"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(o.value)}
                    onChange={() => toggle(o.value)}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span className="truncate">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
